import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import type QRCodeStyling from 'qr-code-styling'
import type { PosterConfig, PosterPurpose } from '@/types'
import { buildBrandedQr, QR_LOGO_SCALE_DEFAULT } from '@/lib/brandedQr'
import { useResolvedQrLogo } from '@/hooks/useBrandQrLogo'
import {
  FOOTER_SIZE_MM,
  HEADLINE_SIZE_MM,
  LAYOUT_DIMS,
  LOGO_SIZES_MM,
  PADDING_MM,
  QR_SIZE_MM,
  SUBHEAD_SIZE_MM,
  getPosterTheme,
  posterQrStyle,
} from './posterThemes'

// What the studio passes in besides the per-purpose PosterConfig — the
// tenant's logo + business name come from the receipt settings (the
// same place receipt printing reads them), not from the poster config.
export interface PosterBrand {
  businessName: string
  logoUrl: string
  /**
   * Tenant's `branded_qr_enabled`. Lives on the brand object so the live
   * preview and both export paths read one value — they must not decide this
   * independently or the print can diverge from the approved preview.
   * Undefined means on, matching the backend's absent-key rule.
   */
  brandedQr?: boolean
}

export interface PosterCanvasHandle {
  /** The mm-anchored poster root (the [data-poster-root] div). */
  posterNode: () => HTMLElement | null
  /** The host wrapper that contains BOTH the <style> tag and the
   *  poster root. PNG export needs this so the scoped CSS travels
   *  with the markup when it's serialised. */
  hostNode: () => HTMLElement | null
}

interface PosterCanvasProps {
  config: PosterConfig
  brand: PosterBrand
  purpose: PosterPurpose
  /** Visual scaling factor for in-page preview. 1 = real size. */
  scale?: number
  /** If true, removes the dashed safe-area frame used in the editor. */
  forPrint?: boolean
}

// PosterCanvas renders the poster at its true physical size (in mm)
// using strictly-scoped CSS (prefixed with a per-instance class) so the
// poster styles never bleed into the admin app and multiple PosterCanvas
// instances on one page don't fight each other.
//
// Light-DOM (not Shadow DOM) is intentional: html-to-image's tree
// walker doesn't traverse Shadow roots, so PNG export needs the poster
// to be in the regular DOM.
//
// The QR is drawn into a <canvas> via qr-code-styling — the same path
// used by AdminMarketingQrSettings — so visual parity with the existing
// QR editor is automatic.
export const PosterCanvas = forwardRef<PosterCanvasHandle, PosterCanvasProps>(
  function PosterCanvas(
    { config, brand, purpose, scale = 1, forPrint = false },
    ref,
  ) {
    const rawId = useId().replace(/[^a-z0-9]/gi, '')
    const scopeClass = `pc-${rawId}`
    const hostRef = useRef<HTMLDivElement | null>(null)
    const posterRef = useRef<HTMLDivElement | null>(null)
    const qrContainerRef = useRef<HTMLDivElement | null>(null)
    const qrRef = useRef<QRCodeStyling | null>(null)
    const { logoDataUrl } = useResolvedQrLogo(brand.logoUrl)
    const brandedQrEnabled = brand.brandedQr !== false

    const theme = useMemo(() => getPosterTheme(config.themeId), [config.themeId])
    const dims = LAYOUT_DIMS[config.layout]
    const padding = PADDING_MM[config.layout]
    const logoMm = LOGO_SIZES_MM[config.layout][config.logoSize]
    const qrMm = QR_SIZE_MM[config.layout]
    const headlineMm = HEADLINE_SIZE_MM[config.layout]
    const subheadMm = SUBHEAD_SIZE_MM[config.layout]
    const footerMm = FOOTER_SIZE_MM[config.layout]

    const accent = useMemo(() => {
      const ov = (config.accentOverride || '').trim()
      return /^#[0-9a-f]{6}$/i.test(ov) ? ov : theme.accent
    }, [config.accentOverride, theme.accent])

    const css = useMemo(
      () => buildPosterCss(scopeClass, theme, accent, config.layout),
      [scopeClass, theme, accent, config.layout],
    )
    const html = useMemo(
      () =>
        buildPosterHtml({
          config,
          brand,
          purpose,
          theme,
          accent,
          dims,
          padding,
          logoMm,
          qrMm,
          headlineMm,
          subheadMm,
          footerMm,
          forPrint,
        }),
      [
        config,
        brand,
        purpose,
        theme,
        accent,
        dims,
        padding,
        logoMm,
        qrMm,
        headlineMm,
        subheadMm,
        footerMm,
        forPrint,
      ],
    )

    useEffect(() => {
      const host = hostRef.current
      if (!host) return
      host.innerHTML = `<style>${css}</style>${html}`
      posterRef.current = host.querySelector('[data-poster-root]') as HTMLDivElement | null
      qrContainerRef.current = host.querySelector('[data-qr-mount]') as HTMLDivElement | null
      paintQr()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [css, html])

    const paintQr = useCallback(() => {
      const container = qrContainerRef.current
      if (!container) return
      // Pixel density: render the canvas at ~12px/mm so it stays crisp
      // both in the on-screen preview and during PNG export at 3× DPI.
      const qrPx = Math.round(qrMm * 11.81)
      const url = (config.qrTargetUrl || '').trim() || 'https://example.com'
      qrRef.current = buildBrandedQr({
        data: url,
        size: qrPx,
        style: posterQrStyle(theme.surfaceInk, theme.surface, accent),
        logoDataUrl,
        logoEnabled: brandedQrEnabled,
        logoScale: QR_LOGO_SCALE_DEFAULT,
      })
      container.innerHTML = ''
      qrRef.current.append(container)
    }, [
      accent,
      config.qrTargetUrl,
      qrMm,
      theme.surface,
      theme.surfaceInk,
      logoDataUrl,
      brandedQrEnabled,
    ])

    useEffect(() => {
      paintQr()
    }, [paintQr])

    useImperativeHandle(
      ref,
      () => ({
        posterNode: () => posterRef.current,
        hostNode: () => hostRef.current,
      }),
      [],
    )

    return (
      <div
        className={`poster-canvas-wrapper ${scopeClass}`}
        style={{
          width: `${dims.wMm * scale}mm`,
          height: `${dims.hMm * scale}mm`,
        }}
      >
        <div
          style={{
            width: `${dims.wMm}mm`,
            height: `${dims.hMm}mm`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <div ref={hostRef} />
        </div>
      </div>
    )
  },
)

// ── HTML / CSS builders ─────────────────────────────────────────────

interface BuildHtmlArgs {
  config: PosterConfig
  brand: PosterBrand
  purpose: PosterPurpose
  theme: ReturnType<typeof getPosterTheme>
  accent: string
  dims: { wMm: number; hMm: number }
  padding: number
  logoMm: number
  qrMm: number
  headlineMm: number
  subheadMm: number
  footerMm: number
  forPrint: boolean
}

export function buildPosterHtml(a: BuildHtmlArgs): string {
  const {
    config,
    brand,
    theme,
    dims,
    padding,
    logoMm,
    qrMm,
    headlineMm,
    subheadMm,
    footerMm,
    forPrint,
  } = a

  const businessName = escapeHtml(brand.businessName || 'Your business')
  const headline = escapeHtml(config.headline)
  const subhead = escapeHtml(config.subhead)
  const footer = escapeHtml(config.footer)
  const qrCaption = escapeHtml(config.qrCaption)
  const logoUrl = brand.logoUrl
  const themeId = theme.id
  const showExplainer = config.showExplainer

  const decorations: string[] = []
  if (theme.decorations.radialGlows) {
    decorations.push('<div class="deco glow glow-a"></div><div class="deco glow glow-b"></div>')
  }
  if (theme.decorations.grain) {
    decorations.push('<div class="deco grain"></div>')
  }
  if (theme.decorations.organicBlobs) {
    decorations.push('<div class="deco blob blob-a"></div><div class="deco blob blob-b"></div>')
  }

  const explainerHtml = showExplainer
    ? `
      <ol class="explainer">
        <li><span class="num">1</span><span class="step-label">Scan the code</span></li>
        <li><span class="num">2</span><span class="step-label">Enter your phone</span></li>
        <li><span class="num">3</span><span class="step-label">Start earning</span></li>
      </ol>
    `
    : ''

  const logoHtml = logoUrl
    ? `<img class="logo-img" src="${escapeAttr(logoUrl)}" alt="${businessName}" crossorigin="anonymous" />`
    : `<div class="logo-mono" aria-label="${businessName}">${monogram(brand.businessName || 'B')}</div>`

  const dividerHtml = theme.decorations.ornamentalDivider
    ? `<div class="divider"><span></span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1"><circle cx="12" cy="12" r="3"></circle></svg><span></span></div>`
    : ''

  const safeAreaCls = forPrint ? '' : 'show-safe-area'

  // Note: every attribute carries an explicit value (e.g. `data-poster-root=""`
  // not bare `data-poster-root`). HTML5 allows bare boolean attributes,
  // but XHTML/XML — which is what `<foreignObject>` parses — does not,
  // and a single bare attribute will reject the whole SVG.
  return `
    <div
      data-poster-root=""
      data-theme="${themeId}"
      data-layout="${config.layout}"
      class="root ${safeAreaCls}"
      style="width:${dims.wMm}mm; height:${dims.hMm}mm; padding:${padding}mm;"
    >
      ${decorations.join('')}
      <header class="brand">
        <div class="logo" style="--logo-mm:${logoMm}mm">${logoHtml}</div>
        <div class="business-name">${businessName}</div>
      </header>

      <main class="body">
        <h1 class="headline" style="font-size:${headlineMm}mm">${headline || '&#160;'}</h1>
        ${dividerHtml}
        <p class="subhead" style="font-size:${subheadMm}mm">${subhead || ''}</p>

        <section class="qr-card">
          <div class="qr-mount" data-qr-mount="" style="width:${qrMm}mm; height:${qrMm}mm"></div>
          ${qrCaption ? `<div class="qr-caption">${qrCaption}</div>` : ''}
        </section>

        ${explainerHtml}
      </main>

      ${footer ? `<footer class="footer" style="font-size:${footerMm}mm">${footer}</footer>` : ''}
    </div>
  `
}

export function buildPosterCss(
  scope: string,
  theme: ReturnType<typeof getPosterTheme>,
  accent: string,
  layout: 'a4' | 'tent' | 'card',
): string {
  const isCard = layout === 'card'
  const s = `.${scope}`
  return `
    ${s} .root { box-sizing: border-box; }
    ${s} .root *, ${s} .root *::before, ${s} .root *::after { box-sizing: border-box; }
    ${s} .root {
      position: relative;
      overflow: hidden;
      background: ${theme.bg};
      color: ${theme.ink};
      font-family: ${theme.sansFont};
      display: ${isCard ? 'grid' : 'flex'};
      ${isCard ? `grid-template-columns: 1fr auto; column-gap: 8mm; align-items: center;` : `flex-direction: column;`}
      isolation: isolate;
    }
    ${s} .root.show-safe-area::after {
      content: '';
      position: absolute;
      inset: 4mm;
      border: 0.6mm dashed ${theme.border};
      border-radius: 2mm;
      pointer-events: none;
      opacity: 0.4;
      z-index: 2;
    }

    ${s} .deco { position: absolute; pointer-events: none; z-index: 0; }
    ${s} .grain {
      inset: 0;
      background-image:
        radial-gradient(rgba(0,0,0,0.06) 1px, transparent 1px),
        radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px);
      background-size: 3px 3px, 5px 5px;
      background-position: 0 0, 1.5px 1.5px;
      mix-blend-mode: overlay;
      opacity: 0.6;
    }
    ${s} .glow {
      width: 70%;
      height: 70%;
      border-radius: 50%;
      filter: blur(40mm);
    }
    ${s} .glow-a { top: -10%; left: -10%; background: ${accent}66; }
    ${s} .glow-b { bottom: -15%; right: -15%; background: ${accent}33; }
    ${s} .blob {
      width: 65%;
      height: 65%;
      border-radius: 50%;
      filter: blur(20mm);
      mix-blend-mode: screen;
    }
    ${s} .blob-a { top: -15%; right: -15%; background: ${accent}55; }
    ${s} .blob-b { bottom: -20%; left: -10%; background: #ffffff22; }

    ${s} .brand {
      position: relative; z-index: 1;
      display: flex;
      ${isCard ? 'flex-direction: column; align-items: flex-start;' : 'flex-direction: column; align-items: center;'}
      gap: 4mm;
      text-align: ${isCard ? 'left' : 'center'};
      ${isCard ? '' : 'margin-bottom: 6mm;'}
    }
    ${s} .logo {
      width: var(--logo-mm);
      height: var(--logo-mm);
      display: flex;
      align-items: center;
      justify-content: center;
      ${theme.decorations.radialGlows ? `box-shadow: 0 0 18mm ${accent}55;` : ''}
      border-radius: ${theme.decorations.hardEdges ? '0' : '50%'};
      overflow: hidden;
      background: ${theme.surface};
      border: ${theme.decorations.hardEdges ? `0.6mm solid ${theme.ink}` : `0.3mm solid ${theme.border}`};
    }
    ${s} .logo-img {
      width: 100%; height: 100%;
      object-fit: contain;
      padding: 8%;
    }
    ${s} .logo-mono {
      width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      font-family: ${theme.serifFont};
      font-size: calc(var(--logo-mm) * 0.45);
      color: ${theme.surfaceInk};
      line-height: 1;
    }
    ${s} .business-name {
      font-family: ${theme.sansFont};
      font-size: ${isCard ? '4mm' : '5mm'};
      letter-spacing: 0.04em;
      text-transform: uppercase;
      font-weight: 600;
      color: ${theme.inkMuted};
    }

    ${s} .body {
      position: relative; z-index: 1;
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: ${isCard ? 'flex-start' : 'center'};
      justify-content: center;
      text-align: ${isCard ? 'left' : 'center'};
      gap: 4mm;
    }
    ${s} .headline {
      font-family: ${theme.serifFont};
      font-weight: 400;
      line-height: ${theme.headlineLeading};
      letter-spacing: -0.012em;
      color: ${theme.ink};
      margin: 0;
      max-width: ${isCard ? '100%' : '85%'};
      ${theme.decorations.hardEdges ? 'text-transform: uppercase; font-family: ' + theme.sansFont + '; font-weight: 800; letter-spacing: -0.02em;' : ''}
    }
    ${s} .subhead {
      font-family: ${theme.sansFont};
      font-weight: 400;
      color: ${theme.inkMuted};
      max-width: ${isCard ? '100%' : '70%'};
      margin: 0;
      line-height: 1.35;
    }
    ${s} .divider {
      display: flex;
      align-items: center;
      gap: 3mm;
      color: ${theme.inkMuted};
      width: 30mm;
      justify-content: center;
      margin: 1mm 0;
    }
    ${s} .divider span {
      flex: 1;
      height: 0.2mm;
      background: ${theme.border};
    }

    ${s} .qr-card {
      position: relative; z-index: 1;
      ${isCard ? 'grid-column: 2; grid-row: 1 / -1; align-self: center;' : 'margin: 4mm 0;'}
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 2.5mm;
      padding: 4mm;
      background: ${theme.surface};
      color: ${theme.surfaceInk};
      border-radius: ${theme.decorations.hardEdges ? '0' : '2.5mm'};
      border: 0.3mm solid ${theme.border};
      ${theme.decorations.hardEdges
        ? 'box-shadow: 1mm 1mm 0 0 currentColor;'
        : `box-shadow: 0 4mm 10mm rgba(0,0,0,0.08), inset 0 0.3mm 0 rgba(255,255,255,0.6);`}
    }
    ${s} .qr-card::before {
      content: '';
      position: absolute;
      inset: 1mm;
      border-radius: ${theme.decorations.hardEdges ? '0' : '1.8mm'};
      border: 0.2mm solid ${accent}55;
      pointer-events: none;
      ${theme.decorations.hardEdges ? 'display: none;' : ''}
    }
    ${s} .qr-mount { display: block; }
    ${s} .qr-mount canvas, ${s} .qr-mount svg {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }
    ${s} .qr-caption {
      font-family: ${theme.sansFont};
      font-size: 3.5mm;
      font-weight: 600;
      letter-spacing: 0.02em;
      color: ${theme.surfaceInk};
      text-align: center;
    }

    ${s} .explainer {
      list-style: none;
      padding: 0;
      margin: 4mm 0 0;
      display: flex;
      ${isCard ? 'flex-direction: column;' : 'flex-direction: row;'}
      gap: ${isCard ? '2mm' : '5mm'};
      justify-content: center;
      align-items: ${isCard ? 'flex-start' : 'center'};
      color: ${theme.ink};
    }
    ${s} .explainer li {
      display: inline-flex;
      align-items: center;
      gap: 2mm;
      font-family: ${theme.sansFont};
      font-size: 4mm;
      font-weight: 500;
    }
    ${s} .num {
      width: 6mm; height: 6mm;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-family: ${theme.serifFont};
      font-style: italic;
      font-size: 4.2mm;
      color: ${accent};
      border: 0.3mm solid ${accent};
      border-radius: 50%;
      flex: 0 0 auto;
    }

    ${s} .footer {
      position: relative; z-index: 1;
      margin-top: ${isCard ? '0' : '6mm'};
      font-family: ${theme.sansFont};
      color: ${theme.inkMuted};
      text-align: ${isCard ? 'left' : 'center'};
      letter-spacing: 0.02em;
    }
  `
}

function escapeHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;')
}

function monogram(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  const letters = parts.map((p) => p[0]?.toUpperCase()).join('')
  return escapeHtml(letters || 'B')
}
